import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search } from 'lucide-react';
import Header from '../components/layout/Header';
import { useAuth } from '../context/AuthContext';
import { warehouseItems, warehouses } from '../services/api';
import WarehouseItemModal from '../components/warehouse/WarehouseItemModal';

const WarehousePage = () => {
  const [items, setItems] = useState([]);
  const [warehousesList, setWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { user, isAdmin, isOfficeAdmin, isWarehouseAdmin } = useAuth();

  const canManageItems = isAdmin() || isOfficeAdmin() || isWarehouseAdmin();
  
  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getAmountTypeLabel = (type) => {
    const types = {
      pcs: 'Pieces',
      meters: 'Meters',
      kilograms: 'Kilograms',
      liters: 'Liters'
    };
    return types[type] || type;
  };

  useEffect(() => {
    fetchItems();
    fetchWarehouses();
  }, []);

  const fetchItems = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { data } = await warehouseItems.getAll();
      setItems(data);
    } catch (err) {
      console.error('Error fetching items:', err);
      setError(err.response?.data?.message || 'Failed to load warehouse items');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWarehouses = async () => {
    try {
      setError(null);
      const { data } = await warehouses.getAll();
      setWarehouses(data);
    } catch (err) {
      console.error('Error fetching warehouses:', err);
      setError(err.response?.data?.message || 'Failed to load warehouses');
    }
  };

  const handleCreateItem = () => {
    setSelectedItem(null);
    setIsModalOpen(true);
  };

  const handleEditItem = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleDeleteItem = async (itemId) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await warehouseItems.delete(itemId);
        setItems(items.filter(item => item._id !== itemId));
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to delete item');
      }
    }
  };

  const handleSaveItem = async (itemData) => {
    try {
      if (selectedItem) {
        const { data } = await warehouseItems.update(selectedItem._id, itemData);
        setItems(items.map(item => item._id === data._id ? data : item));
      } else {
        const { data } = await warehouseItems.create(itemData);
        setItems([...items, data]);
      }
      setIsModalOpen(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save item');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 sm:px-0 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h2 className="text-2xl font-bold text-gray-900">Warehouse Items</h2>
            {canManageItems && (
              <button
                onClick={handleCreateItem}
                className="flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                New Item
              </button>
            )}
          </div>

          <div className="mt-4 max-w-xs">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search items..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item Details
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount Type
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Average Price
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Stock
                </th>
                {canManageItems && (
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
  {filteredItems.map((item) => (
    <tr key={item._id}>
      <td className="px-6 py-4">
        <div className="text-sm font-medium text-gray-900">{item.name}</div>
        {item.description && (
          <div className="text-sm text-gray-500 mt-1">{item.description}</div>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
          {getAmountTypeLabel(item.amountType)}
        </span>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">
          {item.price > 0 ? `$${item.price.toFixed(2)}` : '-'}
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="space-y-1">
          {item.assignedWarehouses
            .filter(assignment => assignment.amount > 0)
            .map((assignment) => (
            <div key={assignment.warehouse._id} className="text-sm">
              <span className="font-medium text-gray-900">{assignment.amount}</span>
              <span className="text-gray-500"> {item.amountType} at </span>
              <span className="text-gray-700">{assignment.warehouse.name}</span>
            </div>
          ))}
          {item.assignedWarehouses.filter(a => a.amount > 0).length === 0 && (
            <div className="text-sm text-gray-500">No stock available</div>
          )}
        </div>
      </td>
      {canManageItems && (
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <button
            onClick={() => handleEditItem(item)}
            className="text-blue-600 hover:text-blue-900 mr-4"
            title="Edit item"
          >
            <Edit className="h-5 w-5" />
          </button>
          <button
            onClick={() => handleDeleteItem(item._id)}
            className="text-red-600 hover:text-red-900"
            title="Delete item"
          >
            <Trash2 className="h-5 w-5" />
          </button>
        </td>
      )}
    </tr>
  ))}
</tbody>
          </table>

          {filteredItems.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No items found matching your search criteria.</p>
            </div>
          )}
        </div>
      </main>

      {isModalOpen && (
        <WarehouseItemModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          item={selectedItem}
          warehouses={warehousesList}
          onSave={handleSaveItem}
        />
      )}
    </div>
  );
};

export default WarehousePage;